import React, { useState, useEffect, useContext } from 'react';
import { Button, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import firebaseNotificationService from '../utils/firebaseNotificationService';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';

const NotificationPermission = ({ show, onHide, onSuccess }) => {
  const { user } = useContext(MyContext);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (show && !isInitialized) {
      initializeService();
    }
  }, [show]);

  const initializeService = async () => {
    try {
      console.log('Initializing notification service...');
      
      const initialized = await firebaseNotificationService.initialize();
      setIsInitialized(true);
      
      if (initialized) {
        const status = firebaseNotificationService.getPermissionStatus();
        setPermissionStatus(status);
      }
    } catch (error) {
      console.error('Failed to initialize notification service:', error);
      setIsInitialized(true);
      setPermissionStatus('default');
    }
  };

  const handleRequestPermission = async () => {
    try {
      setIsSubscribing(true);
      setError(null);

      console.log('Requesting notification permission...');
      
      // Get FCM token from Firebase
      const fcmToken = await firebaseNotificationService.getFCMToken();
      
      console.log('FCM token obtained successfully via permission');
      
      // Send FCM token to backend
      if (user.user_id && fcmToken) {
        await sendFCMTokenToBackend(fcmToken);
      }
      
      setPermissionStatus('granted');
      
      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setError(error.message);
    } finally {
      setIsSubscribing(false);
    }
  };

  const sendFCMTokenToBackend = async (fcmToken) => {
    try {
      console.log('Sending FCM token to backend...');
      
      const response = await fetch(`${backendUrl}/save_fcm_token.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id,
          email: user.email,
          fcm_token: fcmToken,
          device_type: 'web'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save FCM token');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to save FCM token');
      }

      console.log('✅ FCM token saved successfully');
    } catch (error) {
      console.error('❌ Error saving FCM token:', error);
      // Don't throw here as the token was obtained successfully, just couldn't save to backend
    }
  };

  const handleUnsubscribe = async () => {
    try {
      setIsSubscribing(true);
      setError(null);

      await firebaseNotificationService.unsubscribe();
      setPermissionStatus('denied');

      // Remove FCM token from backend
      if (user.user_id) {
        await removeFCMTokenFromBackend();
      }
      
      // Reset state after unsubscribe
      setTimeout(() => {
        setIsInitialized(false);
        setPermissionStatus('default');
        setError(null);
        setIsSubscribing(false);
      }, 1000);
    } catch (error) {
      console.error('Error unsubscribing:', error);
      setError('Failed to disable notifications');
    } finally {
      setIsSubscribing(false);
    }
  };

  const removeFCMTokenFromBackend = async () => {
    try {
      const response = await fetch(`${backendUrl}/remove_fcm_token.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id,
          email: user.email
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove FCM token');
      }

      console.log('FCM token removed successfully');
    } catch (error) {
      console.error('Error removing subscription:', error);
    }
  };

  const getStatusMessage = () => {
    switch (permissionStatus) {
      case 'granted':
        return 'Notifications are enabled! You\'ll receive updates even when the app is closed.';
      case 'denied':
        return 'Notifications are disabled. You can enable them in your browser settings.';
      case 'default':
        return 'Enable notifications to stay updated with important messages and updates.';
      default:
        return 'Notification status unknown.';
    }
  };

  const getStatusVariant = () => {
    switch (permissionStatus) {
      case 'granted':
        return 'success';
      case 'denied':
        return 'warning';
      case 'default':
        return 'info';
      default:
        return 'secondary';
    }
  };

  if (!isInitialized) {
    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faBell} className="me-2" />
            Push Notifications
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading notification service...</p>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faBell} className="me-2" />
          Push Notifications
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant={getStatusVariant()}>
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          {getStatusMessage()}
        </Alert>

        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <div className="mb-3">
          <h6>Benefits of enabling notifications:</h6>
          <ul className="mb-0">
            <li>Get instant updates about therapy sessions</li>
            <li>Receive prayer request notifications</li>
            <li>Stay informed about important messages</li>
            <li>Get reminders for scheduled activities</li>
          </ul>
        </div>

        {permissionStatus === 'default' && (
          <div className="d-grid">
            <Button
              variant="primary"
              onClick={handleRequestPermission}
              disabled={isSubscribing}
            >
              {isSubscribing ? 'Enabling...' : 'Enable Notifications'}
            </Button>
          </div>
        )}

        {permissionStatus === 'granted' && (
          <div className="d-grid">
            <Button
              variant="outline-secondary"
              onClick={handleUnsubscribe}
              disabled={isSubscribing}
            >
              {isSubscribing ? 'Disabling...' : 'Disable Notifications'}
            </Button>
          </div>
        )}

        {permissionStatus === 'denied' && (
          <div className="d-grid">
            <Button
              variant="outline-primary"
              onClick={() => window.open('chrome://settings/content/notifications', '_blank')}
            >
              Open Browser Settings
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default NotificationPermission;
